import React, { useState } from 'react';
import { track } from '../../tracking';

type ColumnSelectorOptions<T> = { label: string | JSX.Element; value: T }[];

function ColumnSelector<T extends string | number>({
    options,
    preSelectedColumns,
    maxColumnsAllowed = options.length,
    mandatoryColumns,
    applySelectedColumns,
    closeCallback,
}: {
    options: ColumnSelectorOptions<T>;
    preSelectedColumns: T[];
    maxColumnsAllowed?: number;
    mandatoryColumns: T[];
    applySelectedColumns: (columns: T[]) => void;
    closeCallback: () => void;
}): JSX.Element {
    const [selectedColumns, setSelectedColumns] = useState(() => {
        const applicableOptions = new Set(options.map((o) => o.value));
        return [
            ...new Set(
                [...mandatoryColumns, ...preSelectedColumns].filter((item) =>
                    applicableOptions.has(item),
                ),
            ),
        ];
    });
    const isMaxRached = selectedColumns.length > maxColumnsAllowed - 1;

    const toggle = (key: T) => {
        track('TOGGLE_QUEUE_COLUMNS', { key: key.toString() });
        if (selectedColumns.includes(key)) {
            if (mandatoryColumns.includes(key)) {
                return;
            }
            setSelectedColumns(selectedColumns.filter((i) => i !== key));
        } else {
            if (isMaxRached) {
                track('MAX_COLUMNS_REACHED_COLUMN_SELECTOR', { key: key.toString() });
                return;
            }
            setSelectedColumns([...selectedColumns, key]);
        }
    };

    return (
        <div
            onClick={(e) => e.stopPropagation()}
            className="flex flex-col bg-white max-h-304 max-w-304 min-w-304 min-h-304"
        >
            <div>
                <div className="flex items-center justify-between px-16  py-10">
                    <p className="space-x-4 text-neutral-black font-700">
                        Column Settings{' '}
                        <span className="text-11">
                            ({selectedColumns.length}/{Math.min(maxColumnsAllowed, options.length)})
                        </span>
                    </p>
                    <div
                        onClick={() => {
                            track('CLOSE_COLUMN_SELECTOR');
                            closeCallback();
                        }}
                        className="flex items-center justify-center w-16 h-16 cursor-pointer"
                    >
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 320 512"
                            className="w-16 h-16 fill-neutral-5 hover:fill-neutral-black"
                        >
                            <path d="M193.94 256L296.5 153.44l21.15-21.15c3.12-3.12 3.12-8.19 0-11.31l-22.63-22.63c-3.12-3.12-8.19-3.12-11.31 0L160 222.06 36.29 98.34c-3.12-3.12-8.19-3.12-11.31 0L2.34 120.97c-3.12 3.12-3.12 8.19 0 11.31L126.06 256 2.34 379.71c-3.12 3.12-3.12 8.19 0 11.31l22.63 22.63c3.12 3.12 8.19 3.12 11.31 0L160 289.94 262.56 392.5l21.15 21.15c3.12 3.12 8.19 3.12 11.31 0l22.63-22.63c3.12-3.12 3.12-8.19 0-11.31L193.94 256z"></path>
                        </svg>
                    </div>
                </div>
            </div>
            <div className="flex-1 p-16 space-y-8 overflow-auto border-t border-b border-neutral-7">
                {options.map((item) => {
                    const isSelected = selectedColumns.includes(item.value);
                    return (
                        <div
                            key={item.value}
                            onClick={(e) => {
                                e.stopPropagation();
                                e.preventDefault();

                                toggle(item.value);
                            }}
                            className="flex items-center justify-between px-8 py-4 bg-actionPrimary bg-opacity-10 rounded-4 cursor-pointer"
                        >
                            <span
                                className={`${
                                    isMaxRached && !isSelected ? 'text-neutral-4' : 'text-neutral-1'
                                }  text-13 font-700`}
                            >
                                {item.label}
                            </span>
                            <div className="flex items-center justify-center w-16 h-16">
                                {isSelected ? (
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        viewBox="0 0 512 512"
                                        className={`w-16 h-16 ${
                                            mandatoryColumns.includes(item.value)
                                                ? ''
                                                : 'fill-actionPrimary'
                                        }`}
                                        fill={mandatoryColumns.includes(item.value) ? 'gray' : ''}
                                    >
                                        <path d="M504 256c0 136.967-111.033 248-248 248S8 392.967 8 256 119.033 8 256 8s248 111.033 248 248zM227.314 387.314l184-184c6.248-6.248 6.248-16.379 0-22.627l-22.627-22.627c-6.248-6.249-16.379-6.249-22.628 0L216 308.118l-70.059-70.059c-6.248-6.248-16.379-6.248-22.628 0l-22.627 22.627c-6.248 6.248-6.248 16.379 0 22.627l104 104c6.249 6.249 16.379 6.249 22.628.001z"></path>
                                    </svg>
                                ) : (
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        viewBox="0 0 512 512"
                                        className="w-16 h-16 fill-neutral-5"
                                    >
                                        <path d="M256 8C119 8 8 119 8 256s111 248 248 248 248-111 248-248S393 8 256 8zm0 448c-110.5 0-200-89.5-200-200S145.5 56 256 56s200 89.5 200 200-89.5 200-200 200z"></path>
                                    </svg>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>
            <div>
                <div className="flex items-center justify-end px-16 py-10">
                    <button
                        onClick={() => {
                            applySelectedColumns(selectedColumns);
                            closeCallback();
                        }}
                        className="px-12 py-8 primary-btn btn--small"
                    >
                        Apply Changes
                    </button>
                </div>
            </div>
        </div>
    );
}

export default ColumnSelector;
